/** @jsxImportSource ./simboluri-jsx */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Termeni, { Estimat, Lamurire } from "../viewer/cost/Termeni";
import PanouBusinessCase from "../viewer/cost/PanouBusinessCase";
import PanouIpoteze from "../viewer/cost/PanouIpoteze";
import PanouScenarii from "../viewer/cost/PanouScenarii";
import PanouOferte from "../viewer/cost/PanouOferte";
import PanouProgram, { scenariulProgramului } from "../viewer/cost/PanouProgram";
import PanouTimelapse from "../viewer/timelapse/PanouTimelapse";
import PanouPiata from "../viewer/cost/PanouPiata";
import { getProfil, type Recomandare } from "../viewer/cost/piata";
import { getProgram, optimizeazaProgram, type Program } from "../viewer/cost/program";
import { getSantier, type OrganizareSantier } from "../viewer/timelapse/santier";
import type { Detaliu, Ipoteze } from "../viewer/cost/businessCase";
import type { ReactNode } from "react";
import { dinServer, getBusinessCase, simuleazaBusinessCase,
         type RaspunsBusinessCase, type RezumatServer } from "../viewer/cost/jetoane";
import { useScenarii, type Abateri, type Scenariu } from "../viewer/cost/scenarii";
import Asistent from "../asistent/Asistent";
import { PASII_COSTURI, unelteCosturi, type ContextCosturi } from "../asistent/unelteCosturi";
import DevizFatada from "../viewer/cost/DevizFatada";
import ListaCantitati from "../viewer/cost/ListaCantitati";
import CatalogBoq from "../viewer/cost/CatalogBoq";
import { getListaCantitati, type ListaCantitati as ListaCantitatiT } from "../viewer/cost/boq";
import type { CampCorectie, DevizFatada as DevizFatadaT } from "../api";
import "../viewer/cost/businessCase.css";
import Marime, { EtichetaNatura } from "./Marime";
import { ArticolDeviz, DetaliuArticol, EstimareStructura, ExtrasArmare, Indicatori, PozitieExtras, Reteta, ReteteConfig, ReteteResponse } from "./tipuri";
import { getEstimare, getExtrasArmare, getRetete, saveRetete } from "./apiStructura";
import GropiPanou from "./GropiPanou";
import { useRolProiect } from "../components/rol";
import SeIncarca from "../components/SeIncarca";
import {
  Moneda,
  bani,
  baniPeM2,
  getMonedaAfisata,
  pretUnitar,
  setCurs,
  setMonedaAfisata,
} from "./bani";
import { hashRetete, refLegatura } from "./legaturi";
import { CORP_TOATE } from "../nav/selectie";
import { numar, t, tn, tx, useLimba, useT } from "../i18n";
import type { Cheie } from "../i18n";

// ---------------------------------------------------------------------------
// «Rețete & costuri»: devizul structurii, în formatul devizului de referință
// ---------------------------------------------------------------------------
// Trei straturi, fiecare vizibil:
//   * DEVIZUL — articole pe capitole, cu preț unitar material și manoperă
//     separat, ca în devizul primit de la Stefan. Fiecare articol se poate
//     expanda până la elementele din care e făcut, iar armătura până la
//     BARĂ (extrasul de armătură).
//   * CALIBRAREA — cum s-a împăcat prețul agregat al referinței cu normele
//     noastre pe elemente: factorul k per familie de lucrare.
//   * REȚETELE ȘI TARIFELE — normele de execuție și parametrii de zonă.

// Codurile enumerabile ale serverului, cu numele lor în catalog
// (`struct.ret.<grup>.<cod>`). Listele sunt ȘI verificarea: cheia se scrie din
// tipul codului, deci un cod adăugat aici fără rând în `ro.ts` oprește build-ul.
// Un cod necunoscut (server mai nou) se arată așa cum a venit.
const SPECIALIZARI = ["dulgheri", "fierari", "betonisti", "muncitori_necalificati"] as const;
const UTILAJE = ["excavator", "pompa_beton", "placa_compactoare", "vibrator_beton"] as const;
const FAMILII_FORMA = ["dreapta", "dreapta_cu_ciocuri", "etrier", "agrafa", "plasa", "spirala"] as const;
const CATEGORII = ["fundatie", "placa_sol", "stalpi", "grinzi", "pereti", "placa"] as const;
const PRECIZII = ["exact", "partial", "forfetar"] as const;
const ARTICOLE_REFERINTA = ["sapatura", "umplutura", "beton", "elicopterizat", "cordon_bentonitic",
  "armatura", "oprire_betonare", "cofraj", "probe_beton", "probe_fier", "accesorii"] as const;
const MATERIALE = ["cofraj_fundatie_m2", "cofraj_elevatie_m2", "cofraj_planseu_m2", "balast_m3",
  "folie_pe_m2", "accesorii_armare_kg"] as const;
const INDIRECTE = ["organizare_santier", "utilaje_ridicare", "indirecte_profit",
  "diverse_neprevazute", "tva"] as const;

/** Numele unui cod din `lista`, prin cheia `struct.ret.<grup>.<cod>`; codul
 *  însuși dacă nu e în listă — el poate fi și un id de element («ST_11») sau
 *  un nume deja tradus de server, deci nu se atinge. */
function eticheta<C extends string>(
  lista: readonly C[],
  cheie: (c: C) => Cheie,
  cod: string | null | undefined,
): string {
  if (!cod) return "";
  return (lista as readonly string[]).includes(cod) ? t(cheie(cod as C)) : cod;
}

/** La fel, pentru cheile configurației (`cofraj_fundatie_m2`): un cod
 *  necunoscut se citește măcar cu spații în loc de `_`. */
function etichetaCheie<C extends string>(
  lista: readonly C[],
  cheie: (c: C) => Cheie,
  cod: string,
): string {
  return (lista as readonly string[]).includes(cod)
    ? t(cheie(cod as C))
    : cod.replace(/_/g, " ");
}

const spec = (id?: string) =>
  id ? eticheta(SPECIALIZARI, (c) => `struct.ret.spec.${c}` as const, id) : "—";
const utilaj = (id: string) =>
  eticheta(UTILAJE, (c) => `struct.ret.utilaj.${c}` as const, id);
const utilaje = (ids?: string[]) =>
  ids && ids.length ? ids.map(utilaj).join(", ") : "—";
const forma = (id?: string) =>
  eticheta(FAMILII_FORMA, (c) => `struct.ret.forma.${c}` as const, id);
const categorie = (id?: string) =>
  eticheta(CATEGORII, (c) => `struct.ret.categorie.${c}` as const, id);
const articolReferinta = (id: string) =>
  etichetaCheie(ARTICOLE_REFERINTA, (c) => `struct.ret.art.${c}` as const, id);
const material = (id: string) =>
  etichetaCheie(MATERIALE, (c) => `struct.ret.mat.${c}` as const, id);
const indirect = (id: string) =>
  etichetaCheie(INDIRECTE, (c) => `struct.ret.ind.${c}` as const, id);

/** Un număr în convenția limbii, cu cel mult `zec` zecimale. */
const nr = (v: number | null | undefined, zec = 1) =>
  numar(v, { maximumFractionDigits: zec });

/** Capitolul unui articol, după cod; serverele vechi n-au codul, iar acolo
 *  terasamentele se recunosc după familie (ca `business_case.din_structura`). */
const capitolul = (a: ArticolDeviz): string =>
  a.capitol_cod ?? (a.tip === "sapatura" || a.tip === "umplutura" ? "terasamente" : "supra");

function BadgePrecizie({ p }: { p?: string }) {
  useT();
  if (!p || p === "exact") return null;
  const cunoscuta = (PRECIZII as readonly string[]).includes(p);
  return (
    <span style={{ marginLeft: 6 }}>
      <Estimat deCe={cunoscuta ? t(`struct.ret.precizie_text.${p as (typeof PRECIZII)[number]}`) : p} />
    </span>
  );
}

export type FilaCosturi = "ipoteze" | "deviz" | "devizul" | "boq" | "retete" | "tarife" | "scenarii" | "oferte" | "program" | "timelapse" | "piata";
export const FILE_COSTURI: readonly FilaCosturi[] = ["ipoteze", "deviz", "devizul", "boq", "retete", "tarife", "scenarii", "oferte", "program", "timelapse", "piata"];

export default function Retete({ projectId, cloneId = null, boqSolutionId = null,
                                 devizFatada = null, laCorectieFatada, seIncarca = false, filaInitiala = null }: {
  projectId: string;
  /** soluția pe care se face LISTA DE CANTITĂȚI: detaliul structurii când e
   *  gata, altfel arhitectura din fir (structura estimată pe m²) */
  boqSolutionId?: string | null;
  /** spațiul detaliului al cărui deviz se arată (corpul rândului din cale);
   *  rețetele și tarifele rămân ale proiectului */
  cloneId?: string | null;
  /** Devizul fațadei arhitecturii din fir — rândul «Anvelopă» și articolele
   *  lui; nu așteaptă structura. */
  devizFatada?: DevizFatadaT | null;
  /** corecția omului pe un rând al devizului fațadei (numai editorii) */
  laCorectieFatada?: (cheie: string, camp: CampCorectie, valoare: number | null) => Promise<void>;
  /** părintele mai așteaptă ceva din ce intră în rezumat (`devizFatada`):
   *  până atunci nu se arată nicio cifră */
  seIncarca?: boolean;
  /** fila cu care se deschide pagina (`?fila=`): dosarul trimite la «Oferte» */
  filaInitiala?: FilaCosturi | null;
}) {
  useT();
  const limba = useLimba();
  const { potScrie } = useRolProiect();
  const [date, setDate] = useState<ReteteResponse | null>(null);
  const [config, setConfig] = useState<ReteteConfig | null>(null);
  const [estimare, setEstimare] = useState<EstimareStructura | null>(null);
  // `null` până se știe dacă ipotezele sunt confirmate: de asta depinde
  // primul ecran (v. mai jos)
  const [tab, setTab] = useState<FilaCosturi | null>(filaInitiala);
  const [moneda, setMoneda] = useState<Moneda>(getMonedaAfisata());
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // spațiul al cărui deviz a răspuns, bine sau cu eroare: fără el, rezumatul
  // s-ar desena întâi din reperul pe m² și s-ar rescrie când sosește devizul
  const cheieEstimare = `${projectId}|${cloneId ?? ""}`;
  const [estimareCitita, setEstimareCitita] = useState<string | null>(null);

  // SCENARIILE întâi: lista de cantități e a scenariului curent, cu rețetele
  // și prețurile lui (`Scenariu.catalog`) și cu macaralele programului lui
  // (`Scenariu.program`). Implicitele ipotezelor vin de la server, cu
  // rezumatul, deci se dau ca funcție, citită la nevoie (v. mai jos).
  const impliciteRef = useRef<Ipoteze | null>(null);
  const sc = useScenarii(projectId, () => impliciteRef.current ?? {}, t("sc.baza"), t("sc.calcat"));
  const scLista = scenariulProgramului(sc);
  const amprentaSc = scLista ? JSON.stringify([sc.curent.catalog ?? null, sc.curent.program ?? null]) : "";
  // până răspund scenariile nu se știe al cui e scenariul curent: o cerere
  // făcută atunci ar fi a bazei, apoi încă una, a scenariului
  const scGata = sc.stare != null;

  // LISTA DE CANTITĂȚI: a soluției de structură (cu devizul ei) sau, până la
  // detaliu, a arhitecturii — pe catalogul scenariului curent. Rezumatul stă
  // pe ea (`dinBoq`), deci pagina o așteaptă ca pe deviz la prima citire —
  // altfel cifrele s-ar rescrie la sosirea ei; la schimbarea scenariului
  // rămâne pe ecran cea veche până sosește cea nouă.
  const [lista, setLista] = useState<ListaCantitatiT | null>(null);
  const [listaEroare, setListaEroare] = useState<string | null>(null);
  const [listaSeIncarca, setListaSeIncarca] = useState(false);
  const cheieLista = `${projectId}|${boqSolutionId ?? ""}`;
  const [listaCitita, setListaCitita] = useState<string | null>(null);
  // REZUMATELE ȘI PROGRAMELE CELORLALTE SCENARII, cerute o dată fiecare; se
  // uită când se reface lista (catalogul proiectului s-a schimbat), iar
  // rezumatul curent se cere din nou (`bcVersiune`)
  const rezumateCerute = useRef(new Map<string, Promise<RezumatServer | null>>());
  const [bcVersiune, setBcVersiune] = useState(0);
  const uitaListele = useCallback(() => {
    rezumateCerute.current = new Map(); programeCerute.current = new Map(); setBcVersiune((v) => v + 1);
  }, []);
  // PROGRAMUL fiecărui scenariu, pentru durata din «Scenarii»: cerut o singură
  // dată pe scenariu (pe bază, pentru cele care nu schimbă nici lista, nici
  // programul); se uită odată cu listele
  const programeCerute = useRef(new Map<string, Promise<Program | null>>());
  // …și, odată venite, durata lor intră în rezumatul fiecărui scenariu
  const cheieProgram = useCallback((s: Scenariu) =>
    scenariulProgramului({ curent: s } as Parameters<typeof scenariulProgramului>[0])
      ? `${s.id}|${JSON.stringify(s.program ?? null)}|${JSON.stringify(s.catalog ?? null)}` : "baza", []);
  const programPentru = useCallback((s: Scenariu): Promise<Program | null> => {
    if (!boqSolutionId) return Promise.resolve(null);
    const k = cheieProgram(s);
    let p = programeCerute.current.get(k);
    if (!p) {
      p = getProgram(projectId, boqSolutionId, { scenariu: k === "baza" ? null : s.id }).catch(() => null);
      programeCerute.current.set(k, p);
    }
    return p;
  }, [projectId, boqSolutionId, cheieProgram]);
  // programul scenariului curent se cere de la început: durata lui e în rezultate
  useEffect(() => { if (scGata) void programPentru(sc.curent); }, [programPentru, sc.curent, scGata]);
  // organizarea de șantier a scenariului (documentul planificării), pentru asistent
  const santiereCerute = useRef(new Map<string, Promise<OrganizareSantier | null>>());
  const santierPentru = useCallback((s: Scenariu): Promise<OrganizareSantier | null> => {
    if (!boqSolutionId) return Promise.resolve(null);
    const k = cheieProgram(s);
    let p = santiereCerute.current.get(k);
    if (!p) {
      p = getSantier(projectId, boqSolutionId, { scenariu: k === "baza" ? null : s.id }).catch(() => null);
      santiereCerute.current.set(k, p);
    }
    return p;
  }, [projectId, boqSolutionId, cheieProgram]);
  const cerereLista = useRef(0);
  const incarcaLista = useCallback(async (refa = false) => {
    if (!boqSolutionId) { setLista(null); setListaCitita(`${projectId}|`); return; }
    if (!scGata) return;
    const nr = ++cerereLista.current;
    setListaSeIncarca(true);
    setListaEroare(null);
    if (refa) uitaListele();
    try {
      const l = await getListaCantitati(projectId, boqSolutionId, refa, { scenariu: scLista });
      if (nr === cerereLista.current) setLista(l);
    } catch (e) {
      if (nr !== cerereLista.current) return;
      setListaEroare((e as Error).message);
      setLista(null);
    } finally {
      if (nr === cerereLista.current) {
        setListaSeIncarca(false);
        setListaCitita(`${projectId}|${boqSolutionId}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, boqSolutionId, scLista, amprentaSc, scGata, uitaListele]);
  useEffect(() => { void incarcaLista(); }, [incarcaLista]);
  // altă soluție a listei: ce s-a cerut pe cea veche nu mai e bun. Rezumatul
  // curent îl cere singur, fiindcă atârnă de soluție; la deschidere nu e nimic
  // de uitat, iar o versiune nouă l-ar fi cerut a doua oară.
  const solutiaListei = useRef(`${projectId}|${boqSolutionId ?? ""}`);
  useEffect(() => {
    const k = `${projectId}|${boqSolutionId ?? ""}`;
    if (k === solutiaListei.current) return;
    solutiaListei.current = k;
    rezumateCerute.current = new Map(); programeCerute.current = new Map();
    if (scGata) void programPentru(sc.curent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, boqSolutionId]);

  // Răspunsul unui spațiu părăsit se aruncă: devizul cerut fără spațiu, cât
  // registrul nu sosise, putea veni după cel al detaliului și îl înlocuia.
  useEffect(() => {
    let viu = true;
    getRetete(projectId, cloneId)
      .then((r) => {
        if (!viu) return;
        setDate(r);
        setConfig(r.config);
        setCurs(r.config?.curs_lei_eur?.valoare);
      })
      .catch((e) => { if (viu) setError(e.message); });
    getEstimare(projectId, cloneId, { rezumat: true })
      .then((e) => { if (viu) setEstimare(e); })
      .catch(() => { if (viu) setEstimare(null); })
      .finally(() => { if (viu) setEstimareCitita(`${projectId}|${cloneId ?? ""}`); });
    return () => { viu = false; };
  }, [projectId, cloneId]);

  // Recalcularea devizului, ca funcție: panoul gropilor o cere după ce omul a
  // schimbat groapa. `await`, nu „fire and forget" — butonul de acolo rămâne
  // dezactivat până se întoarce, altfel omul apasă de trei ori.
  const reincarcaEstimarea = useCallback(async () => {
    await getEstimare(projectId, cloneId, { rezumat: true }).then(setEstimare)
      .catch(() => setEstimare(null));
    setBcVersiune((v) => v + 1);
  }, [projectId, cloneId]);

  // FAȚADA SE DESFACE ȘI FĂRĂ STRUCTURĂ: devizul ei e al arhitecturii
  const detaliuFatada = devizFatada && devizFatada.articole.length
    ? { fatada: <DevizFatada deviz={devizFatada} laCorectie={laCorectieFatada} /> } : {};

  // REZUMATUL DE BUSINESS CASE VINE DE LA SERVER (`api/business_case.py`,
  // motorul `cost_model.business_case`), pe scenariul curent — cu abaterile,
  // lista și programul lui — și pe soluția listei. Se cere din nou la fiecare
  // schimbare a scenariilor (`rev`), a devizului (rețete, gropi), a listei sau
  // a devizului fațadei; limba și moneda nu-l cer din nou: textele se scriu
  // aici, la desen. Până sosește cel nou rămâne pe ecran cel vechi.
  const scId = sc.stare?.scenarii.some((x) => x.id === sc.curent.id) ? sc.curent.id : null;
  const [bc, setBc] = useState<RaspunsBusinessCase | null>(null);
  const [bcEroare, setBcEroare] = useState<string | null>(null);
  const [bcCitit, setBcCitit] = useState<string | null>(null);
  const cheieBc = `${projectId}|${boqSolutionId ?? ""}`;
  const cerereBc = useRef(0);
  const revScenarii = sc.stare?.rev ?? -1;
  const revFatada = devizFatada?.total_direct_eur ?? null;
  // Se cere O DATĂ la deschidere: după scenarii (al cui e) și după devizul
  // fațadei (`seIncarca`, intră în rezumat). Fiecare cerere în plus e un
  // rezumat întreg socotit de server, chiar dacă răspunsul se aruncă aici.
  useEffect(() => {
    if (!scGata || seIncarca) return;
    const nr_ = ++cerereBc.current;
    // ce s-a schimbat pentru scenariul curent s-a schimbat și pentru celelalte
    rezumateCerute.current = new Map();
    if (!boqSolutionId) { setBc(null); setBcCitit(cheieBc); return; }
    getBusinessCase(projectId, boqSolutionId, { scenariu: scId })
      .then((r) => { if (nr_ === cerereBc.current) { setBc(r); setBcEroare(null); } })
      .catch((e) => { if (nr_ === cerereBc.current) setBcEroare((e as Error).message); })
      .finally(() => { if (nr_ === cerereBc.current) setBcCitit(cheieBc); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, boqSolutionId, scId, revScenarii, bcVersiune, revFatada, scGata, seIncarca]);
  // limba și moneda: textele și sumele se scriu la desen
  const rezCurent = useMemo(() => (bc ? dinServer(bc.rezumat) : null), [bc, limba, moneda]);
  const implicite = bc?.implicite ?? null;
  impliciteRef.current = implicite;
  const verificate = sc.stare?.verificate ?? null;
  // COMPARAȚIA SCENARIILOR ȘI ASISTENTUL: rezumatul fiecărui scenariu, de la
  // server, pe lista și programul lui, cerut o singură dată pe abaterile lui
  const rezumatPentru = useCallback((s: Scenariu): Promise<RezumatServer | null> => {
    if (!boqSolutionId) return Promise.resolve(null);
    const k = `${s.id}|${JSON.stringify(s.ipoteze)}|${JSON.stringify(s.catalog ?? null)}|${JSON.stringify(s.program ?? null)}`;
    let p = rezumateCerute.current.get(k);
    if (!p) {
      p = getBusinessCase(projectId, boqSolutionId, { scenariu: s.id }).then((r) => r.rezumat, () => null);
      rezumateCerute.current.set(k, p);
    }
    return p;
  }, [projectId, boqSolutionId]);
  /** Rezumatul scenariului curent cu alte abateri peste, fără să salveze (asistentul). */
  const simuleaza = useCallback((abateri: Abateri) => {
    if (!boqSolutionId) return Promise.reject(new Error("fără soluție"));
    return simuleazaBusinessCase(projectId, boqSolutionId, { scenariu: scId, abateri });
  }, [projectId, boqSolutionId, scId]);
  const structuraSv = bc?.proiect.structura ?? null;

  // PRIMUL ECRAN: ipotezele, până le confirmă cineva; apoi rezultatele
  useEffect(() => {
    if (tab == null && sc.stare) setTab(sc.stare.verificate ? "deviz" : "ipoteze");
  }, [tab, sc.stare]);
  // PIAȚA DIN ZONĂ propune prețul apartamentelor pe ecranul «Ipoteze»: profilul
  // zonei se cere la fiecare deschidere a lui (o colectare poate să se fi
  // terminat între timp); fără oferte culese, ecranul rămâne cum era
  const [recPiata, setRecPiata] = useState<Recomandare | null>(null);
  useEffect(() => {
    if (tab !== "ipoteze") return;
    let viu = true;
    getProfil(projectId).then((p) => { if (viu) setRecPiata(p?.recomandare ?? null); })
      .catch(() => { if (viu) setRecPiata(null); });
    return () => { viu = false; };
  }, [tab, projectId]);
  // un ecran schimbat din josul altuia (butonul de sub ipoteze) se deschide de
  // la capăt, nu din mijlocul lui — sau din golul de după el
  const taburi = useRef<HTMLDivElement | null>(null);
  const primulTab = useRef(true);
  useEffect(() => {
    if (tab == null) return;
    if (primulTab.current) { primulTab.current = false; return; }
    const r = taburi.current?.getBoundingClientRect();
    if (r && r.top < 0) taburi.current?.scrollIntoView({ block: "start" });
  }, [tab]);

  // ASISTENTUL citește pagina prin referință: uneltele se leagă o dată, la
  // deschiderea discuției, dar trebuie să vadă scenariul de ACUM
  const ctxRef = useRef({ bc, rezCurent, sc, estimare, devizFatada, rezumatPentru, simuleaza, programPentru, santierPentru });
  ctxRef.current = { bc, rezCurent, sc, estimare, devizFatada, rezumatPentru, simuleaza, programPentru, santierPentru };
  const unelte = useMemo(() => {
    const c: ContextCosturi = {
      stare: () => ({ proiect: ctxRef.current.bc?.proiect ?? null, rez: ctxRef.current.rezCurent,
                      scenarii: ctxRef.current.sc.stare, curent: ctxRef.current.sc.curent,
                      estimare: ctxRef.current.estimare, devizFatada: ctxRef.current.devizFatada ?? null }),
      rezumatPentru: (s) => ctxRef.current.rezumatPentru(s),
      // programul scenariului, cum îl vede și fila «Program»: durata e a lui
      program: (s) => ctxRef.current.programPentru(s),
      // organizarea de șantier a scenariului: fazele, macaralele așezate, camioanele
      santier: (s) => ctxRef.current.santierPentru(s),
      simuleaza: (a) => ctxRef.current.simuleaza(a),
      creeaza: potScrie ? (a, m) => ctxRef.current.sc.creeaza(a, m) : null,
      alege: potScrie ? (id) => ctxRef.current.sc.alege(id) : null,
      // optimizatorul programului: pe soluția listei; fără listă, unealta o spune
      ...(boqSolutionId ? { optimizeaza: (b) => optimizeazaProgram(projectId, boqSolutionId, b) } : {}),
    };
    return unelteCosturi(c);
  }, [potScrie, projectId, boqSolutionId]);

  async function onSave(cfg: ReteteConfig | null) {
    setBusy(true);
    setError(null);
    try {
      const r = await saveRetete(projectId, cfg);
      setDate(r);
      setConfig(r.config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      getEstimare(projectId, cloneId, { rezumat: true }).then(setEstimare)
        .catch(() => setEstimare(null));
      void incarcaLista();
      setBcVersiune((v) => v + 1);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // REZUMATUL NU ATÂRNĂ DE CATALOGUL DE REȚETE.
  //
  // Ecranul se oprea aici când rețetele nu se puteau citi — bun cât devizul era
  // lista de articole, care chiar are nevoie de ele. Rezumatul de business case
  // n-are: el iese din teren, CUT și niște prețuri unitare. Pe un proiect fără
  // structură calculată, oprirea asta ascundea tocmai singurul deviz care se
  // putea arăta.
  if (seIncarca || estimareCitita !== cheieEstimare || listaCitita !== cheieLista || bcCitit !== cheieBc) return <SeIncarca />;
  /** Rezumatul, sau de ce nu e: fără soluție de arhitectură n-are pe ce se face. */
  const panouBc = (detalii: Partial<Record<Detaliu, ReactNode>>) => (rezCurent && bc
    ? <PanouBusinessCase rez={rezCurent} banda={bc.banda ?? []} note={bc.note} detalii={detalii}
                         scenariu={sc.curent.nume} laScenarii={() => setTab("scenarii")} />
    : <div className="sg-error">{bcEroare ?? t("bc.ui.fara_solutie")}</div>);
  if (error && !date) {
    return (
      <>
        <div className="sg-error">{error}</div>
        {panouBc(detaliuFatada)}
      </>
    );
  }
  if (!date || !config || tab == null) return <SeIncarca />;

  const catalog = date.catalog;
  // Numele și sursa referinței, în limba cererii; serverele mai vechi le dau
  // doar în configurație.
  const referintaNume = date.referinta_eticheta?.nume ?? config.referinta?.nume;
  const referintaSursa = date.referinta_eticheta?.sursa ?? config.referinta?.sursa;

  return (
    <div className="pj-step-page">
      <h2>
        {t("struct.ret.deviz_retete_executie")}
        {(tab === "retete" || tab === "tarife") && (
          <Lamurire text={tx("struct.ret.intro", { referinta: referintaNume })} />
        )}
      </h2>

      <div className="pf-row" style={{ marginBottom: 10 }}>
        <label>{t("struct.ret.afiseaza_sumele")}</label>
        <span className="pf-input">
          <select
            value={moneda}
            onChange={(e) => {
              const m = e.target.value as Moneda;
              setMonedaAfisata(m);
              setMoneda(m);
            }}
          >
            <option value="EUR">{t("struct.ret.moneda_eur")}</option>
            <option value="RON">
              {t("struct.ret.moneda_ron", {
                curs: nr(config.curs_lei_eur?.valoare, 2),
                la: config.curs_lei_eur?.la,
              })}
            </option>
          </select>
        </span>
      </div>

      <div className="pf-tabs" ref={taburi}>
        {(
          [
            ["ipoteze", t("costuri.tab.ipoteze")],
            ["deviz", t("costuri.tab.rezultate")],
            ["devizul", t("costuri.tab.devizul")],
            ["boq", t("costuri.tab.boq")],
            ["retete", t("struct.ret.tab_retete")],
            ["tarife", t("struct.ret.preturi_zona_norme", { p1: date.implicit ? "" : t("struct.ret.modificate") })],
            ["scenarii", t("costuri.tab.scenarii")],
            ["oferte", t("costuri.tab.oferte")],
            ["program", t("costuri.tab.program")],
            ["timelapse", t("costuri.tab.timelapse")],
            ["piata", t("costuri.tab.piata")],
          ] as const
        ).map(([k, eticheta]) => (
          <button
            key={k}
            className={`pf-tab ${tab === k ? "active" : ""}`}
            onClick={() => setTab(k)}
          >
            {eticheta}
            {k === "ipoteze" && (
              <span className={`ip-pill ${verificate ? "ip-pill-ok" : "ip-pill-amb"}`}
                    style={{ marginLeft: 8 }}>
                {t(verificate ? "costuri.tab.verificate" : "costuri.tab.de_verificat")}</span>
            )}
            {k === "scenarii" && (sc.stare?.scenarii.length ?? 0) > 1 && (
              <span className="ip-pill ip-pill-mut" style={{ marginLeft: 8 }}>
                {sc.stare!.scenarii.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* DEVIZUL E REZUMATUL DE BUSINESS CASE.
          Cerut de Stefan, în forma fișei de calcul: «asta e devizul nostru
          acum». Devizul de execuție pe articole — cantități măsurate ×
          prețuri din rețete — nu s-a pierdut: săpătura și structura
          rezumatului se iau din el, iar articolele lui se văd desfăcând
          rândul («datele vechi să apară tot în formatul nou, dar când
          expandezi drop-down-ul corespunzător»).
          V. `viewer/cost/businessCase.ts`. */}
      {tab === "ipoteze" && (rezCurent && implicite ? (
        <PanouIpoteze rez={rezCurent} implicite={implicite} sc={sc} potScrie={potScrie} piata={recPiata}
                      laRezultate={() => setTab("deviz")} laScenarii={() => setTab("scenarii")} />
      ) : <div className="sg-error">{bcEroare ?? t("bc.ui.fara_solutie")}</div>)}

      {tab === "scenarii" && (rezCurent ? (
        <PanouScenarii rezCurent={rezCurent} rezumatPentru={rezumatPentru} programPentru={programPentru}
                       sc={sc} potScrie={potScrie} laRezultate={() => setTab("deviz")} />
      ) : <div className="sg-error">{bcEroare ?? t("bc.ui.fara_solutie")}</div>)}

      {/* OFERTELE: un document tras aici face un scenariu nou cu prețurile lui
          (v. `viewer/cost/PanouOferte.tsx`, `control-plane/api/oferte.py`). */}
      {tab === "oferte" && (
        <PanouOferte projectId={projectId} solutionId={boqSolutionId} potScrie={potScrie} sc={sc}
                     laScenariu={(id) => { void sc.alege(id).then(() => setTab("deviz")).catch(() => {}); }}
                     dupaAplicare={uitaListele} />
      )}

      {/* PROGRAMUL DE EXECUȚIE: durata și efectivele, din lista de cantități a
          aceleiași soluții (v. `viewer/cost/PanouProgram.tsx`,
          `control-plane/api/program.py`). */}
      {tab === "program" && (
        <PanouProgram projectId={projectId} solutionId={boqSolutionId} sc={sc} potScrie={potScrie} />
      )}

      {/* TIMELAPSE-UL: șantierul săptămână cu săptămână, pe un singur desen 3D,
          din desfășurarea pachetelor programului (v. `viewer/timelapse/`). */}
      {tab === "timelapse" && (
        <PanouTimelapse projectId={projectId} solutionId={boqSolutionId} sc={sc}
                        saptamanaInitiala={Number(new URLSearchParams(location.search).get("sapt")) || null} />
      )}

      {/* VÂNZĂRILE DIN ZONĂ: ofertele de vânzare din jurul terenului, culese
          din anunțurile publice — ale proiectului, nu ale unei soluții (v.
          `viewer/cost/PanouPiata.tsx`, `control-plane/api/piata.py`). */}
      {tab === "piata" && <PanouPiata projectId={projectId} potScrie={potScrie} />}

      {tab === "deviz" && panouBc(estimare && structuraSv && bc ? {
        ...detaliuFatada,
        terasamente: (
          <DevizTerasamente estimare={estimare} projectId={projectId}
                            cloneId={cloneId} reincarca={reincarcaEstimarea}
                            volumGroapaM3={structuraSv.izolate && !structuraSv.groapaComuna
                              ? Math.max(bc.groapa.volumM3 - structuraSv.groapaM3, 0) : 0} />
        ),
        structura: (
          <DevizStructura estimare={estimare} projectId={projectId}
                          cloneId={cloneId} reincarca={reincarcaEstimarea} />
        ),
      } : detaliuFatada)}


      {/* DEVIZUL, SINGUR, separat de lista de cantități: devizul de execuție pe articole — săpătura,
          structura, anvelopa și taxele de acces —, fără rezumatul de business
          case de pe «Rezultate». Aceleași tabele, deschise direct. Se poate
          deschide din adresă cu `?fila=devizul`. */}
      {tab === "devizul" && (
        <div className="bc-detaliu-singur">
          {estimare ? (
            <>
              <DevizTerasamente estimare={estimare} projectId={projectId}
                                cloneId={cloneId} reincarca={reincarcaEstimarea}
                                volumGroapaM3={structuraSv && bc && structuraSv.izolate && !structuraSv.groapaComuna
                                  ? Math.max(bc.groapa.volumM3 - structuraSv.groapaM3, 0) : 0} />
              <DevizStructura estimare={estimare} projectId={projectId}
                              cloneId={cloneId} reincarca={reincarcaEstimarea} />
            </>
          ) : (
            <div className="sg-muted" style={{ marginBottom: 12 }}>{t("costuri.dupa_detaliu")}</div>
          )}
          {devizFatada && devizFatada.articole.length > 0 && (
            <DevizFatada deviz={devizFatada} laCorectie={laCorectieFatada} />
          )}
        </div>
      )}

      {/* LISTA DE CANTITĂȚI: toate specialitățile, pe articole, pe soluția
          de detaliu (`cloneId`); rețetele și resursele ei au catalogul lor,
          global + copiile proiectului — v. `viewer/cost/ListaCantitati.tsx`. */}
      {tab === "boq" && (
        <ListaCantitati projectId={projectId} solutionId={boqSolutionId}
                        scenariu={scLista ? { id: scLista, nume: sc.curent.nume } : null}
                        lista={lista} eroare={listaEroare} seIncarca={listaSeIncarca}
                        reincarca={incarcaLista} laCatalog={() => setTab("retete")} potScrie={potScrie} />
      )}

      {/* O SINGURĂ PAGINĂ DE REȚETE: cele 6 de execuție
          ale structurii — pe care stă devizul structurii, cu tarifele din
          «Prețuri, zonă & norme» — și cele ~150 ale listei de cantități, cu
          copiile proiectului, filtrate împreună. */}
      {tab === "retete" && (
        <CatalogBoq projectId={projectId} potScrie={potScrie} lista={lista} solutieId={boqSolutionId}
                    laSalvat={() => void incarcaLista(true)}
                    scenariu={{ id: sc.curent.id, nume: sc.curent.nume, catalog: sc.curent.catalog,
                                salveaza: (c) => sc.schimbaCatalog(sc.curent.id, c) }}
                    structura={{
                      retete: catalog,
                      detaliu: (id, inchide) => (
                        <CarteReteta catalog={catalog} reteta={catalog.find((r) => r.id === id)}
                                     onDeschide={(x) => { if (x == null) inchide(); }}
                                     familii={date.familii_pret} />
                      ),
                    }} />
      )}

      {tab === "tarife" && (
        <Tarife
          config={config}
          implicit={date.implicit}
          presete={date.presete_zona}
          referintaNume={referintaNume}
          referintaSursa={referintaSursa}
          busy={busy}
          saved={saved}
          error={error}
          onChange={setConfig}
          onSave={() => onSave(config)}
          onReset={() => onSave(null)}
        />
      )}

      {/* ASISTENTUL DEVIZULUI: explică cifrele și face scenarii noi; plutește
          în colț, ca tabelul de sub el să nu se miște când se deschide */}
      <Asistent projectId={projectId} cod={projectId} potScrie={potScrie} unde="costuri"
                unelte={unelte} titlu={t("costuri.asistent")} subtitlu={sc.curent.nume}
                prietenos pasi={PASII_COSTURI} />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sumarul: costul direct, defalcarea și indicatorii de sănătate
// ---------------------------------------------------------------------------

function Sumar({ estimare }: { estimare: EstimareStructura }) {
  useT();
  const ind = indicatoriDin(estimare);
  const indirecte = indirecteDin(estimare);
  return (
    <>
      <div className="st-cards">
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.ret.card_cost_direct")}
            <EtichetaNatura cheie="cost_structura" />
            <Marime cheie="cost_structura" />
          </div>
          <div className="st-card-value">{bani(estimare.total_direct_eur)}</div>
          <div className="st-card-sub">
            {t("struct.ret.card_cost_direct_sub")} ·{" "}
            {estimare.eur_pe_m2 != null
              ? t("struct.ret.p1_desfasurat", { p1: baniPeM2(estimare.eur_pe_m2) })
              : "—"}
            <BadgePrecizie p={estimare.precizie} />
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.ret.card_materiale")}<Marime cheie="cost_materiale" />
          </div>
          <div className="st-card-value">{bani(estimare.total_material_eur)}</div>
          <div className="st-card-sub">
            {t("struct.ret.card_materiale_sub")}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.ret.card_manopera")}<Marime cheie="cost_manopera" />
          </div>
          <div className="st-card-value">{bani(estimare.total_manopera_eur)}</div>
          <div className="st-card-sub">
            {t("struct.ret.card_manopera_sub")}
          </div>
        </div>
        {indirecte.length > 0 && (
          <div className="st-card">
            <div className="st-card-label">
              {t("struct.ret.card_cu_indirecte")}<Marime cheie="cost_cu_indirecte" />
            </div>
            <div className="st-card-value">{bani(estimare.total_eur)}</div>
            <div className="st-card-sub">
              {indirecte
                .map((i) => `${i.eticheta} ${i.procent}%`)
                .join(" · ")}
            </div>
          </div>
        )}
      </div>

      {ind?.kg_pe_m3 != null && (
        <div className="sg-muted" style={{ margin: "6px 2px 12px" }}>
          {tx("struct.ret.repere", {
            kg: nr(ind.kg_pe_m3),
            cofraj: nr(ind.cofraj_m2_pe_m3, 2),
            kg_ref: nr(ind.referinta?.kg_pe_m3, 2),
            cofraj_ref: nr(ind.referinta?.cofraj_m2_pe_m3, 2),
          })}{" "}
          {ind.in_banda === false && tx("struct.ret.repere_in_afara")}{" "}
          {t("struct.ret.repere_total", {
            beton: nr(ind.beton_m3),
            otel: nr(ind.otel_kg, 0),
            cofraj: nr(ind.cofraj_m2, 0),
          })}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Devizul pe articole, cu expand
// ---------------------------------------------------------------------------

/** INDICATORII DEVIZULUI. O clădire singură îi are gata; devizul asamblat al
 *  unei soluții cu corpuri nu — îi au corpurile, și se adună aici. */
function indicatoriDin(estimare: EstimareStructura): Indicatori | null {
  if (estimare.indicatori) return estimare.indicatori;
  const ale = Object.values(estimare.corpuri ?? {})
    .map((c) => c.indicatori).filter((x): x is Indicatori => !!x);
  if (!ale.length) return null;
  const beton = ale.reduce((s, x) => s + (x.beton_m3 ?? 0), 0);
  const otel = ale.reduce((s, x) => s + (x.otel_kg ?? 0), 0);
  const cofraj = ale.reduce((s, x) => s + (x.cofraj_m2 ?? 0), 0);
  const arie = estimare.arie_desfasurata_m2;
  return {
    referinta: ale[0].referinta,
    beton_m3: beton, otel_kg: otel, cofraj_m2: cofraj,
    kg_pe_m3: beton ? otel / beton : undefined,
    cofraj_m2_pe_m3: beton ? cofraj / beton : undefined,
    beton_m3_pe_m2: arie ? beton / arie : undefined,
    otel_kg_pe_m2: arie ? otel / arie : undefined,
    in_banda: ale.every((x) => x.in_banda !== false),
  };
}

/** Procentele de după costul direct: ale devizului, sau ale primului corp. */
const indirecteDin = (estimare: EstimareStructura) =>
  estimare.indirecte?.length ? estimare.indirecte
    : Object.values(estimare.corpuri ?? {}).find((c) => c.indirecte?.length)?.indirecte ?? [];

/** Numele unui corp pe articolele devizului asamblat: tronsonul, sau
 *  pseudo-corpul «complex» — groapa pe tot subsolul. */
const numeCorp = (c: string) => (c === "complex" ? t("bc.det.corp_complex") : c);

/** SĂPĂTURA, sub rândul ei din rezumat: articolele de terasamente și harta
 *  gropilor. La tronsoanele calculate izolat, groapa ÎNTREGULUI subsol e în
 *  deviz o singură dată (pseudo-corpul «complex»); când serverul n-o are
 *  încă, rezumatul o socotește din amprentă, deci pământul care pleacă e al
 *  ei plus al fundațiilor, iar groapa autorizată se alege o dată, pe tot
 *  volumul, nu pe fiecare tronson. Pe devizul unui corp dintr-un complex
 *  săpătura lipsește cu motiv: e a soluției, pe «Toate corpurile». */
function DevizTerasamente({ estimare, projectId, cloneId, reincarca, volumGroapaM3 }: {
  estimare: EstimareStructura;
  projectId: string;
  cloneId: string | null;
  reincarca: () => Promise<void>;
  /** groapa socotită de rezumat, m³ — 0 când devizul o are deja */
  volumGroapaM3: number;
}) {
  useT();
  const evacuareDeviz = estimare.articole.reduce((s, a) => s + (a.evacuare_m3 ?? 0), 0);
  const peTot = volumGroapaM3 > 0;
  const sc = estimare.sapatura_complexului;
  const peComplex = estimare.sapatura_din === "complex";
  // devizul unui CORP: săpătura e a soluției
  const corpInComplex = peComplex && !estimare.corpuri && !!sc?.solutie;
  const nrc = (v: number | null | undefined, z = 0) =>
    v == null ? "—" : numar(v, { minimumFractionDigits: z, maximumFractionDigits: z });
  return (
    <>
      <div className="bc-art-sus">
        <span className="bc-art-titlu">{t("bc.det.titlu_terasamente")}</span>
        {corpInComplex && (
          <Lamurire text={t("bc.det.sapatura_corp_in_complex", {
            volum: sc?.volum_m3 != null
              ? t("bc.det.sapatura_corp_volum", { n: nrc(sc.volum_m3) }) : "" })} />
        )}
        {peComplex && !!estimare.corpuri && sc?.volum_m3 != null && (
          <Lamurire text={t("bc.det.sapatura_complex_text", {
            volum: nrc(sc.volum_m3), arie: nrc(sc.groapa_m2), h: nrc(sc.H_m, 2),
            n: sc.nr_adanciri ?? 0,
            corpuri: sc.volum_corpuri_m3 != null
              ? t("bc.det.sapatura_complex_corpuri", { n: nrc(sc.volum_corpuri_m3) }) : "" })} />
        )}
        <span className="sg-muted">{t("bc.det.din_deviz", { versiune: estimare.versiune_tarife })}</span>
        {corpInComplex && (
          <a href={hashRetete(projectId, refLegatura(sc!.solutie, CORP_TOATE))}>
            {t("bc.det.sapatura_corp_link")}
          </a>
        )}
      </div>
      <TabelDeviz estimare={estimare} projectId={projectId} cloneId={cloneId}
                  reincarca={reincarca} capitole={["terasamente"]}
                  gropiPeArticol={!peTot} />
      {peTot && (
        <>
          <div className="rc-sect">
            {t("bc.det.gropi_tot")}
            <Lamurire text={t("bc.det.gropi_tot_text", {
              groapa: nr(volumGroapaM3, 0), fundatii: nr(evacuareDeviz, 0),
              tot: nr(volumGroapaM3 + evacuareDeviz, 0) })} />
          </div>
          <GropiPanou projectId={projectId} volumM3={volumGroapaM3 + evacuareDeviz}
                      peRegenerare={reincarca} />
        </>
      )}
    </>
  );
}

/** STRUCTURA, sub rândul ei din rezumat: sumarul devizului de execuție,
 *  articolele infrastructurii, ale suprastructurii și lucrările conexe —
 *  fiecare până la elemente și, la armătură, până la bară —, procentele de
 *  după costul direct și calibrarea pe referință. */
function DevizStructura({ estimare, projectId, cloneId, reincarca }: {
  estimare: EstimareStructura;
  projectId: string;
  cloneId: string | null;
  reincarca: () => Promise<void>;
}) {
  useT();
  const lipsa = estimare.lipsa ?? [];
  return (
    <>
      <div className="bc-art-sus">
        <span className="bc-art-titlu">{t("bc.det.titlu_structura")}</span>
        <span className="sg-muted">{t("bc.det.din_deviz", { versiune: estimare.versiune_tarife })}</span>
      </div>
      <Sumar estimare={estimare} />
      {lipsa.length > 0 && (
        <div className="rc-lipsa">
          {t("struct.ret.deviz_incomplet", { lipsa: lipsa.join("; ") })}
        </div>
      )}
      <TabelDeviz estimare={estimare} projectId={projectId} cloneId={cloneId}
                  reincarca={reincarca} capitole={["infra", "supra", "conexe", "acces"]} />
    </>
  );
}

/** DEVIZUL PE ARTICOLE, pe capitole, cu fiecare articol desfăcut până la
 *  elementele din care e făcut. Stă sub rândurile rezumatului de business case
 *  (`DevizTerasamente`, `DevizStructura`): `capitole` alege ce capitole se
 *  văd, iar procentele de după costul direct, articolele declarate și
 *  calibrarea se arată numai cu structura — săpătura e un singur capitol. */
export function TabelDeviz({
  estimare,
  projectId,
  cloneId = null,
  reincarca,
  capitole: alese,
  gropiPeArticol = true,
}: {
  estimare: EstimareStructura;
  projectId: string;
  /** spațiul detaliului — extrasul de armătură se cere de acolo */
  cloneId?: string | null;
  /** recalculează devizul — îl dă părintele, care știe de unde vine */
  reincarca: () => Promise<void>;
  /** codurile capitolelor arătate (`terasamente`, `infra`, `supra`,
   *  `conexe`); fără ele, toate */
  capitole?: string[];
  /** harta gropilor sub fiecare articol de săpătură */
  gropiPeArticol?: boolean;
}) {
  useT();
  const [deschis, setDeschis] = useState<number | null>(null);
  const [corp, setCorp] = useState<string | null>(null);
  const cuStructura = !alese || alese.some((c) => c !== "terasamente");

  const articole = useMemo(
    () => estimare.articole.filter((a) => !alese || alese.includes(capitolul(a))),
    [estimare, alese],
  );
  // tronsoanele, la devizul unei soluții cu corpuri — cu suma fiecăruia
  const corpuri = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of articole) {
      if (a.corp) m.set(a.corp, (m.get(a.corp) ?? 0) + a.total_material_eur + a.total_manopera_eur);
    }
    return [...m.entries()].sort((x, y) => x[0].localeCompare(y[0], undefined, { numeric: true }));
  }, [articole]);
  const vazute = corp ? articole.filter((a) => a.corp === corp) : articole;
  const capitole = useMemo(() => {
    const m = new Map<string, ArticolDeviz[]>();
    for (const a of vazute) {
      const l = m.get(a.capitol) ?? [];
      l.push(a);
      m.set(a.capitol, l);
    }
    return [...m.entries()];
  }, [vazute]);
  const totalMaterial = vazute.reduce((s, a) => s + a.total_material_eur, 0);
  const totalManopera = vazute.reduce((s, a) => s + a.total_manopera_eur, 0);
  const indirecte = estimare.indirecte ?? [];

  if (!articole.length) {
    return <div className="sg-muted" style={{ margin: "10px 2px" }}>{t("bc.det.fara_articole")}</div>;
  }

  return (
    <>
      {corpuri.length > 1 && (
        <div className="rc-chips" style={{ marginBottom: 8 }} role="group"
             aria-label={t("bc.det.tronsoane")}>
          <button type="button" className={`rc-chip ${corp == null ? "active" : ""}`}
                  onClick={() => { setCorp(null); setDeschis(null); }}>
            {tn("bc.det.toate_tronsoanele#n", corpuri.length)}
          </button>
          {corpuri.map(([c, v]) => (
            <button key={c} type="button" className={`rc-chip ${corp === c ? "active" : ""}`}
                    onClick={() => { setCorp(corp === c ? null : c); setDeschis(null); }}>
              {numeCorp(c)} · {bani(v)}
            </button>
          ))}
        </div>
      )}
      <table className="sg-table rc-deviz">
        <thead>
          <tr>
            <th>{t("struct.ret.th_nr")}</th>
            <th>{t("struct.ret.th_articol")}</th>
            <th><Termeni text={t("struct.ret.th_um")} /></th>
            <th>{t("struct.ret.th_cantitate")}</th>
            <th><Termeni text={t("struct.ret.th_pu_material")} /></th>
            <th><Termeni text={t("struct.ret.p_u_manopera")} /></th>
            <th>{t("struct.ret.th_total_material")}</th>
            <th>{t("struct.ret.total_manopera")}</th>
          </tr>
        </thead>
        <tbody>
          {capitole.map(([cap, art]) => (
            <Fragment key={cap}>
              <tr className="rc-capitol">
                <td colSpan={8}>
                  <strong>{cap.toUpperCase()}</strong>
                </td>
              </tr>
              {art.map((a) => (
                <Fragment key={a.nr}>
                  <tr
                    onClick={() => setDeschis(deschis === a.nr ? null : a.nr)}
                    style={{ cursor: "pointer" }}
                    title={a.sursa_pret}
                  >
                    <td className="sg-mono">{a.nr}</td>
                    <td>
                      {deschis === a.nr ? "▾ " : "▸ "}
                      {a.corp && corp == null && (
                        <span className="rc-chip" style={{ marginRight: 6 }}>{numeCorp(a.corp)}</span>
                      )}
                      {a.articol}
                      <BadgePrecizie p={a.precizie} />
                    </td>
                    <td className="sg-mono">{a.um}</td>
                    <td className="sg-mono">{nr(a.cantitate, 2)}</td>
                    <td className="sg-mono">
                      {a.pu_material_eur ? pretUnitar(a.pu_material_eur) : "—"}
                    </td>
                    <td className="sg-mono">
                      {a.pu_manopera_eur ? pretUnitar(a.pu_manopera_eur) : "—"}
                    </td>
                    <td className="sg-mono">{bani(a.total_material_eur)}</td>
                    <td className="sg-mono">{bani(a.total_manopera_eur)}</td>
                  </tr>
                  {deschis === a.nr && (
                    <tr>
                      <td colSpan={8} className="rc-detaliu">
                        <DetaliuArticolRand
                          articol={a}
                          projectId={projectId}
                          cloneId={cloneId}
                        />
                        {/* Săpătura e singurul articol care trimite ceva la
                            groapă, deci singurul sub care harta are sens. */}
                        {gropiPeArticol && !!a.evacuare_m3 && (
                          <GropiPanou
                            projectId={projectId}
                            volumM3={a.evacuare_m3}
                            peRegenerare={reincarca}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6}>
              <strong>{t("struct.ret.total_cost_direct")}</strong>
            </td>
            <td className="sg-mono">
              <strong>{bani(totalMaterial)}</strong>
            </td>
            <td className="sg-mono">
              <strong>{bani(totalManopera)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>

      {cuStructura && !corp && indirecte.length > 0 && (
        <>
          <div className="rc-sect">
            {t("struct.ret.cost_direct_pret")}
            <Lamurire text={t("struct.ret.procente_aplicate_dupa")} />
          </div>
          <table className="sg-table">
            <tbody>
              <tr>
                <td>{t("struct.ret.cost_direct")}</td>
                <td className="sg-mono">—</td>
                <td className="sg-mono">{bani(estimare.total_direct_eur)}</td>
              </tr>
              {indirecte.map((i) => (
                <tr key={i.cheie}>
                  <td>{i.eticheta}</td>
                  <td className="sg-mono">{i.procent} %</td>
                  <td className="sg-mono">{bani(i.valoare_eur)}</td>
                </tr>
              ))}
              <tr>
                <td>
                  <strong>{t("struct.ret.total")}</strong>
                </td>
                <td />
                <td className="sg-mono">
                  <strong>{bani(estimare.total_eur)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {cuStructura && (estimare.articole_lipsa ?? []).length > 0 && (
        <>
          <div className="rc-sect">
            {t("struct.ret.deviz_declarat_ascuns")}
            <Lamurire text={t("struct.ret.articolele_jos_exista")} />
          </div>
          <table className="sg-table">
            <tbody>
              {estimare.articole_lipsa.map((a, i) => (
                <tr key={i}>
                  <td>{a.articol}</td>
                  <td className="sg-mono">{a.um}</td>
                  <td>{a.motiv}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {cuStructura && <Calibrare estimare={estimare} />}
    </>
  );
}

function DetaliuArticolRand({
  articol,
  projectId,
  cloneId = null,
}: {
  articol: ArticolDeviz;
  projectId: string;
  cloneId?: string | null;
}) {
  useT();
  // Codul, când serverul îl trimite; altfel numele, care la serverele vechi
  // vine mereu românesc.
  const esteArmatura = articol.tip
    ? articol.tip === "armatura"
    : articol.articol.startsWith("Armătură");
  return (
    <>
      <div className="sg-muted" style={{ marginBottom: 6 }}>
        <strong>{t("struct.ret.unde_vine_pretul")}</strong> {articol.sursa_pret}
      </div>
      {articol.nota && (
        <div className="sg-muted" style={{ marginBottom: 6 }}>
          {articol.nota}
        </div>
      )}
      {articol.detaliu.map((d, i) =>
        d.grup ? (
          <div key={i} style={{ marginBottom: 8 }}>
            <div className="rc-sect" style={{ marginTop: 4 }}>
              {d.grup}
            </div>
            <table className="sg-table">
              <tbody>
                {(d.rand ?? []).map((r, j) => (
                  <tr key={j}>
                    <td>{forma(r.eticheta)}</td>
                    <td className="sg-mono">{nr(r.kg, 0)} kg</td>
                    <td className="sg-mono">
                      {r.pu_material != null && (
                        <>{t("struct.ret.material_pu", { pu: pretUnitar(r.pu_material, "kg") })}</>
                      )}
                      {r.pu_manopera != null && (
                        <>
                          {t("struct.ret.manopera_pu", { pu: pretUnitar(r.pu_manopera, "kg") })}
                          {r.ore_om_kg != null && (
                            <span className="sg-muted">
                              {" "}{t("struct.ret.ore_om_kg_paren", { v: nr(r.ore_om_kg, 6) })}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null,
      )}
      {articol.detaliu.some((d) => !d.grup) && (
        <table className="sg-table">
          <tbody>
            {articol.detaliu
              .filter((d) => !d.grup)
              .map((d: DetaliuArticol, i) => (
                <tr key={i}>
                  <td>{categorie(d.eticheta)}</td>
                  <td className="sg-mono">
                    {d.m3 != null && `${nr(d.m3, 2)} m³`}
                    {d.m2 != null && `${nr(d.m2, 1)} m²`}
                    {d.kg != null && `${nr(d.kg, 0)} kg`}
                    {d.cantitate != null && `${nr(d.cantitate, 1)} ${d.um ?? ""}`}
                  </td>
                  <td className="sg-mono">
                    {d.manopera_um != null &&
                      t("struct.ret.manopera_p1_um", { p1: pretUnitar(d.manopera_um) })}
                    {d.derivat_eur != null && bani(d.derivat_eur)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
      {/* Extrasul se servește pe spațiul unei clădiri; la devizul asamblat
          al unei soluții cu corpuri, pozițiile sunt ale fiecărui corp. */}
      {esteArmatura && !articol.corp && <ExtrasBare projectId={projectId} cloneId={cloneId} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Extrasul de armătură — poziție cu poziție
// ---------------------------------------------------------------------------

function ExtrasBare({ projectId, cloneId = null }: {
  projectId: string;
  cloneId?: string | null;
}) {
  useT();
  const [cat, setCat] = useState<string>("grinzi");
  const [date, setDate] = useState<ExtrasArmare | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [element, setElement] = useState<string | null>(null);

  useEffect(() => {
    setDate(null);
    getExtrasArmare(projectId, { categorie: cat }, cloneId)
      .then(setDate)
      .catch((e) => setEroare((e as Error).message));
  }, [projectId, cloneId, cat]);

  const elemente = useMemo(() => {
    const m = new Map<string, PozitieExtras[]>();
    for (const p of date?.pozitii ?? []) {
      const l = m.get(p.element) ?? [];
      l.push(p);
      m.set(p.element, l);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [date]);

  return (
    <div style={{ marginTop: 10 }}>
      <div className="rc-sect">
        {t("struct.ret.extrasul_armatura_bara")}
        <Lamurire text={t("struct.ret.lungimile_includ_ancorar")} />
      </div>
      <div className="pf-row">
        <label>{t("struct.ret.categoria_element")}</label>
        <span className="pf-input">
          <select value={cat} onChange={(e) => {
            setElement(null);
            setCat(e.target.value);
          }}>
            {CATEGORII.map((k) => (
              <option key={k} value={k}>
                {categorie(k)}
              </option>
            ))}
          </select>
        </span>
      </div>
      {eroare && <div className="sg-error">{eroare}</div>}
      {!date && !eroare && <div className="sg-muted">{t("struct.ret.incarca")}</div>}
      {date && (
        <>
          <div className="sg-muted" style={{ margin: "4px 0" }}>
            {tn("struct.ret.extras_sumar#n", date.nr_filtrate ?? 0, {
              total: date.nr_pozitii,
              kg: nr(date.kg, 0),
              spor: nr((date.spor_pierderi - 1) * 100, 0),
            })}
            {date.pct_calculat != null &&
              t("struct.ret.extras_calculat", { pct: nr(date.pct_calculat) })}
          </div>
          <div className="rc-chips" style={{ marginBottom: 6 }}>
            {elemente.slice(0, 80).map(([el, poz]) => (
              <button
                key={el}
                className={`rc-chip ${element === el ? "active" : ""}`}
                onClick={() => setElement(element === el ? null : el)}
              >
                {el} · {nr(poz.reduce((s, p) => s + p.kg, 0), 0)} kg
              </button>
            ))}
          </div>
          {element && (
            <table className="sg-table">
              <thead>
                <tr>
                  <th>{t("struct.ret.th_poz")}</th>
                  <th>{t("struct.ret.th_rol")}</th>
                  <th>Ø</th>
                  <th>{t("struct.ret.th_forma")}</th>
                  <th>{t("struct.ret.th_buc")}</th>
                  <th>{t("struct.ret.th_lungime_bara")}</th>
                  <th>kg</th>
                  <th>{t("struct.ret.compune_lungimea")}</th>
                </tr>
              </thead>
              <tbody>
                {(elemente.find(([e]) => e === element)?.[1] ?? []).map(
                  (p, i) => (
                    <tr key={i}>
                      <td className="sg-mono">{p.poz}</td>
                      <td>{p.rol}</td>
                      <td className="sg-mono">Ø{p.phi_mm}</td>
                      <td>{forma(p.familie)}</td>
                      <td className="sg-mono">{p.n}</td>
                      <td className="sg-mono">{nr(p.l_bara_m, 2)} m</td>
                      <td className="sg-mono">{nr(p.kg, 1)}</td>
                      <td style={{ fontSize: 12 }}>{p.detaliu}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calibrarea: cum s-a împăcat prețul agregat al referinței cu normele noastre
// ---------------------------------------------------------------------------

function Calibrare({ estimare }: { estimare: EstimareStructura }) {
  useT();
  // devizul asamblat al unei soluții cu corpuri n-are calibrarea lui: e a
  // fiecărui corp, cu aceeași referință — se arată a primului
  const sursa = estimare.calibrare ? estimare
    : Object.values(estimare.corpuri ?? {}).find((c) => c.calibrare);
  const familii = Object.entries(sursa?.calibrare ?? {}).filter(
    ([, v]) => v.pret_referinta_manopera != null,
  );
  if (!familii.length) return null;
  return (
    <>
      <div className="rc-sect">
        {t("struct.ret.calibrare_titlu")}
        <Lamurire text={`${t("struct.ret.calibrare_explicatie")} ${t("struct.ret.calibrare_otel", { factor: nr(sursa?.normalizare_otel, 3) })}`} />
      </div>
      <table className="sg-table">
        <thead>
          <tr>
            <th>{t("struct.ret.th_familie")}</th>
            <th>{t("struct.ret.th_cantitate")}</th>
            <th>{t("struct.ret.derivat_norme")}</th>
            <th>{t("struct.ret.pret_referinta")}</th>
            <th>{t("struct.ret.k_manopera")}</th>
            <th>{t("struct.ret.th_k_material")}</th>
          </tr>
        </thead>
        <tbody>
          {familii.map(([k, v]) => (
            <tr key={k}>
              <td>{v.nume}</td>
              <td className="sg-mono">
                {nr(v.cantitate)} {v.um}
              </td>
              <td className="sg-mono">
                {v.derivat_unitar_manopera != null
                  ? `${pretUnitar(v.derivat_unitar_manopera)}/${v.um}`
                  : "—"}
              </td>
              <td className="sg-mono">
                {v.pret_referinta_manopera != null
                  ? `${pretUnitar(v.pret_referinta_manopera)}/${v.um}`
                  : "—"}
              </td>
              <td className="sg-mono">
                {nr(v.k_manopera, 3)}
                {v.k_suspect && " ⚠"}
              </td>
              <td className="sg-mono">
                {v.k_material != null && v.k_material !== 1
                  ? nr(v.k_material, 3)
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

// ---------------------------------------------------------------------------
// Cartea de rețete (normele de execuție)
// ---------------------------------------------------------------------------

function CarteReteta({
  catalog,
  reteta,
  onDeschide,
  familii,
}: {
  catalog: Reteta[];
  reteta: Reteta | null | undefined;
  onDeschide: (id: string | null) => void;
  familii: ReteteResponse["familii_pret"];
}) {
  useT();
  if (!reteta)
    return (
      <>
        <div className="sg-muted" style={{ margin: "10px 2px" }}>
          {t("struct.ret.retetele_stabilesc_nivel")}
        </div>
        <div className="rc-grid">
          {catalog.map((r) => (
            <button
              key={r.id}
              className="rc-card"
              onClick={() => onDeschide(r.id)}
            >
              <div className="rc-card-head">
                <span className="rc-cod">{r.id.split("_")[0]}</span>
                <span className="rc-categorie">{r.categorie}</span>
              </div>
              <div className="rc-nume">{r.nume}</div>
              <div className="rc-chips">
                {r.activitati.map((a) => (
                  <span key={a.cod} className="rc-chip">
                    {a.nume.split("(")[0].trim()}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      </>
    );

  return (
    <div className="st-findings">
      <button onClick={() => onDeschide(null)}>{t("struct.ret.retetele")}</button>
      <h3 style={{ marginTop: 8 }}>{reteta.nume}</h3>
      <div className="sg-muted">{reteta.categorie}</div>

      <div className="rc-sect">{t("struct.ret.activitatile_normele_lor")}</div>
      <table className="sg-table">
        <thead>
          <tr>
            <th>{t("struct.ret.th_activitate")}</th>
            <th>{t("struct.ret.th_echipa")}</th>
            <th>{t("struct.ret.th_utilaje")}</th>
            <th><Termeni text={t("struct.ret.th_um")} /></th>
            <th>{t("struct.ret.th_ore_om_um")}</th>
            <th>{t("struct.ret.plateste")}</th>
          </tr>
        </thead>
        <tbody>
          {reteta.activitati.map((a) => (
            <tr key={a.cod}>
              <td>
                {a.nume}
                {a.nota && (
                  <div className="sg-muted" style={{ fontSize: 12 }}>
                    {a.nota}
                  </div>
                )}
              </td>
              <td>
                {spec(a.specializare)}
                {a.muncitori ? ` × ${a.muncitori}` : ""}
              </td>
              <td>{utilaje(a.utilaje)}</td>
              <td className="sg-mono">{a.um ?? "—"}</td>
              <td className="sg-mono">
                {a.din_extras
                  // armătura n-are o normă pe kg: orele vin din extrasul de
                  // armătură, pe familii de formă
                  ? <span className="sg-muted" style={{ fontFamily: "inherit" }} title={t("struct.ret.ore_din_extras_t")}>
                      {t("struct.ret.ore_din_extras")}</span>
                  : nr(a.ore_om_um, 6)}
              </td>
              <td>
                {a.din_extras
                  ? t("struct.ret.din_extras")
                  : familii[a.familie_pret ?? ""]?.nume ?? a.familie_pret}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="rc-sect">{t("struct.ret.unde_vin_cantitatile")}</div>
      <div>{reteta.corelare}</div>

      <div className="rc-sect">{t("struct.ret.coduri_deviz")}</div>
      <div className="rc-chips">
        {reteta.coduri_deviz.map((c) => (
          <span key={c} className="rc-chip">
            {c}
          </span>
        ))}
      </div>

      <div className="rc-sect">{t("struct.ret.flux")}</div>
      <div>{reteta.flux}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Prețuri, zonă & norme — configurația editabilă (overlay peste implicit)
// ---------------------------------------------------------------------------

function Num({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return (
    <input
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const v = parseFloat(e.target.value.replace(",", "."));
        if (!Number.isNaN(v)) onChange(v);
      }}
    />
  );
}

function Rand({
  label,
  hint,
  unit,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  unit: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="pf-row">
      <label>
        {label}
        {hint && <span className="sg-muted"> — {hint}</span>}
      </label>
      <span className="pf-input">
        <Num value={value} onChange={onChange} />
        <span>{unit}</span>
      </span>
    </div>
  );
}

function Tarife({
  config,
  implicit,
  presete,
  referintaNume,
  referintaSursa,
  busy,
  saved,
  error,
  onChange,
  onSave,
  onReset,
}: {
  config: ReteteConfig;
  implicit: boolean;
  presete: ReteteResponse["presete_zona"];
  /** numele și sursa referinței, deja în limba ecranului */
  referintaNume?: string;
  referintaSursa?: string;
  busy: boolean;
  saved: boolean;
  error: string | null;
  onChange: (c: ReteteConfig) => void;
  onSave: () => void;
  onReset: () => void;
}) {
  useT();
  const { potScrie } = useRolProiect();
  const set = (path: (string | number)[], v: number | string) => {
    const c = JSON.parse(JSON.stringify(config)) as ReteteConfig;
    let o: Record<string, unknown> = c as unknown as Record<string, unknown>;
    for (const p of path.slice(0, -1))
      o = o[p as string] as Record<string, unknown>;
    o[path[path.length - 1] as string] = v;
    onChange(c);
  };

  const z = config.zona;
  const art = config.referinta?.articole ?? {};

  return (
    <div className="st-findings">
      <div className="rc-sect">
        {t("struct.ret.folosesc_preturile_refer")}
        <Lamurire text={tx("struct.ret.tarife_intro", { nume: referintaNume, sursa: referintaSursa })} />
      </div>
      <div className="pf-row">
        <label>{t("struct.ret.mod_calcul")}</label>
        <span className="pf-input">
          <select
            value={config.mod_pret}
            onChange={(e) => set(["mod_pret"], e.target.value)}
          >
            <option value="calibrat">{t("struct.ret.mod_calibrat")}</option>
            <option value="referinta">{t("struct.ret.mod_referinta")}</option>
            <option value="norme">{t("struct.ret.mod_norme")}</option>
          </select>
        </span>
      </div>

      <div className="rc-sect">{t("struct.ret.preturile_referinta_eur")}</div>
      {Object.entries(config.referinta?.beton_material_m3 ?? {}).map(
        ([clasa, v]) => (
          <Rand
            key={clasa}
            label={t("struct.ret.beton_material", { clasa })}
            hint={t("struct.ret.franco_santier_zona")}
            unit="€/m³"
            value={v}
            onChange={(x) =>
              set(["referinta", "beton_material_m3", clasa], x)
            }
          />
        ),
      )}
      {Object.entries(art).map(([k, a]) => (
        <div key={k}>
          {a.material != null && (
            <Rand
              label={t("struct.ret.articol_material", { articol: articolReferinta(k) })}
              unit={`€/${a.um}`}
              value={a.material}
              onChange={(x) => set(["referinta", "articole", k, "material"], x)}
            />
          )}
          {a.manopera != null && (
            <Rand
              label={t("struct.ret.p1_manopera", { p1: articolReferinta(k) })}
              hint={t("struct.ret.include_utilajele_punere")}
              unit={`€/${a.um}`}
              value={a.manopera}
              onChange={(x) => set(["referinta", "articole", k, "manopera"], x)}
            />
          )}
        </div>
      ))}

      <div className="rc-sect">
        {t("struct.ret.zona_proiectului")}
        <Lamurire text={t("struct.ret.modelul_transport_ramane")} />
      </div>
      <div className="pf-row">
        <label>{t("struct.ret.presetare")}</label>
        <span className="pf-input">
          <select
            value={z.nume}
            onChange={(e) => {
              const p = Object.values(presete).find(
                (x) => x.nume === e.target.value,
              );
              if (!p) return;
              const c = JSON.parse(JSON.stringify(config)) as ReteteConfig;
              c.zona.nume = p.nume;
              c.zona.indice_manopera = p.indice_manopera;
              c.zona.spor_aprovizionare_pct = p.spor_aprovizionare_pct;
              onChange(c);
            }}
          >
            {Object.values(presete).map((p) => (
              <option key={p.nume} value={p.nume}>
                {p.eticheta ?? p.nume}
              </option>
            ))}
            {!Object.values(presete).some((p) => p.nume === z.nume) && (
              <option value={z.nume}>{z.nume}</option>
            )}
          </select>
        </span>
      </div>
      <Rand
        label={t("struct.ret.indice_manopera_zonei")}
        hint={t("struct.ret.1_00_bucuresti")}
        unit="×"
        value={z.indice_manopera}
        onChange={(x) => set(["zona", "indice_manopera"], x)}
      />
      <Rand
        label={t("struct.ret.spor_aprovizionare")}
        hint={t("struct.ret.otel_cofraj_accesorii")}
        unit="%"
        value={z.spor_aprovizionare_pct}
        onChange={(x) => set(["zona", "spor_aprovizionare_pct"], x)}
      />
      <Rand
        label={t("struct.ret.distanta_statia_betoane")}
        unit="km"
        value={z.beton.dist_statie_km}
        onChange={(x) => set(["zona", "beton", "dist_statie_km"], x)}
      />
      <Rand
        label={t("struct.ret.raza_transport_inclus")}
        unit="km"
        value={z.beton.inclus_sub_km}
        onChange={(x) => set(["zona", "beton", "inclus_sub_km"], x)}
      />
      <Rand
        label={t("struct.ret.tarif_cifa_peste")}
        unit="€/km"
        value={z.beton.tarif_eur_km}
        onChange={(x) => set(["zona", "beton", "tarif_eur_km"], x)}
      />
      <Rand
        label={t("struct.ret.prag_turnare_mica")}
        hint={t("struct.ret.sub_el_plateste")}
        unit="m³"
        value={z.beton.prag_cursa_mica_m3}
        onChange={(x) => set(["zona", "beton", "prag_cursa_mica_m3"], x)}
      />
      <Rand
        label={t("struct.ret.taxa_cifa_incompleta")}
        unit={t("struct.ret.um_cursa")}
        value={z.beton.taxa_cursa_mica_eur}
        onChange={(x) => set(["zona", "beton", "taxa_cursa_mica_eur"], x)}
      />
      <Rand
        label={t("struct.ret.distanta_groapa_autoriza")}
        unit="km"
        value={z.pamant.dist_groapa_km}
        onChange={(x) => set(["zona", "pamant", "dist_groapa_km"], x)}
      />
      <Rand
        label={t("struct.ret.tarif_basculanta")}
        unit="€/km"
        value={z.pamant.tarif_eur_km}
        onChange={(x) => set(["zona", "pamant", "tarif_eur_km"], x)}
      />
      <Rand
        label={t("struct.ret.taxa_depozitare_pamant")}
        hint={t("struct.ret.implicit_0_santier")}
        unit={t("struct.ret.um_tona")}
        value={z.pamant.taxa_depozitare_eur_t}
        onChange={(x) => set(["zona", "pamant", "taxa_depozitare_eur_t"], x)}
      />
      {z.pamant.raza_max_km != null && (
        <>
          <Rand label={t("struct.ret.raza_max_groapa")} hint={t("struct.ret.raza_max_hint")} unit="km"
                value={z.pamant.raza_max_km} onChange={(x) => set(["zona", "pamant", "raza_max_km"], x)} />
          <Rand label={t("struct.ret.tarif_plafon_pamant")} hint={t("struct.ret.tarif_plafon_hint")}
                unit={t("struct.ret.um_tona")} value={z.pamant.tarif_plafon_eur_t ?? 0}
                onChange={(x) => set(["zona", "pamant", "tarif_plafon_eur_t"], x)} />
        </>
      )}

      {/* TAXA DE ACCES A CAMIOANELOR (București A/B): zona o dă serverul din
          datele lotului (nu se alege aici — se schimbă pe Localizare); aici
          sunt ipotezele din care ies zilele-camion și zona de referință. */}
      {z.acces && (
        <>
          <div className="rc-sect">
            {t("struct.ret.acces_titlu")}
            <Lamurire text={t("struct.ret.acces_explicatie")} />
          </div>
          <div className="pf-row">
            <label>{t("struct.ret.acces_zona")}</label>
            <span className="pf-input">
              {z.acces.cunoscut ? (
                <>
                  <strong>{t(z.acces.zona ? (`loc.acces.${z.acces.zona}` as Cheie) : "loc.acces.niciuna")}</strong>
                  <span className="sg-muted"> · {t(z.acces.sursa === "om" ? "loc.acces.de_om" : "loc.acces.automat")}
                    {z.acces.la_limita && z.acces.distanta_limita_m != null
                      ? ` · ${t("loc.acces.la_limita", { m: nr(z.acces.distanta_limita_m, 0) })}` : ""}
                    {" · "}{t("struct.ret.acces_unde")}</span>
                </>
              ) : <span className="sg-muted">{t("struct.ret.acces_necunoscut")}</span>}
            </span>
          </div>
          <div className="pf-row">
            <label>{t("struct.ret.acces_referinta")}</label>
            <span className="pf-input">
              <select value={z.acces.referinta ?? ""}
                      onChange={(e) => set(["zona", "acces", "referinta"], e.target.value)}>
                <option value="B">{t("loc.acces.B")}</option>
                <option value="A">{t("loc.acces.A")}</option>
                <option value="">{t("struct.ret.acces_ref_niciuna")}</option>
              </select>
            </span>
          </div>
          <div className="pf-row">
            <label>{t("struct.ret.acces_regim_pamant")}</label>
            <span className="pf-input">
              <select value={z.acces.regim_pamant}
                      onChange={(e) => set(["zona", "acces", "regim_pamant"], e.target.value)}>
                <option value="luna">{t("struct.ret.acces_regim_luna")}</option>
                <option value="zi">{t("struct.ret.acces_regim_zi")}</option>
              </select>
            </span>
          </div>
          <Rand label={t("struct.ret.acces_viteza")} unit={t("struct.ret.um_km_h")}
                value={z.acces.viteza_kmh} onChange={(x) => set(["zona", "acces", "viteza_kmh"], x)} />
          <Rand label={t("struct.ret.acces_minute")} hint={t("struct.ret.acces_minute_hint")} unit={t("struct.ret.um_min")}
                value={z.acces.minute_moarte_cursa} onChange={(x) => set(["zona", "acces", "minute_moarte_cursa"], x)} />
          <Rand label={t("struct.ret.acces_ore")} unit={t("struct.ret.um_ore")}
                value={z.acces.ore_lucru_zi} onChange={(x) => set(["zona", "acces", "ore_lucru_zi"], x)} />
          <Rand label={t("struct.ret.acces_zile_luna")} unit={t("struct.ret.um_zile")}
                value={z.acces.zile_lucratoare_luna} onChange={(x) => set(["zona", "acces", "zile_lucratoare_luna"], x)} />
          <Rand label={t("struct.ret.acces_camion")} unit="t"
                value={z.acces.camion_tone} onChange={(x) => set(["zona", "acces", "camion_tone"], x)} />
          <Rand label={t("struct.ret.acces_cofraj")} unit="m²"
                value={z.acces.cofraj_m2_pe_camion} onChange={(x) => set(["zona", "acces", "cofraj_m2_pe_camion"], x)} />
          <Rand label={t("struct.ret.acces_beton_zi")} unit="m³"
                value={z.acces.beton_pe_zi_turnare_m3} onChange={(x) => set(["zona", "acces", "beton_pe_zi_turnare_m3"], x)} />
        </>
      )}

      <div className="rc-sect">
        {t("struct.ret.otelul_structura_pretulu")}
        <Lamurire text={t("struct.ret.referinta_da_singur")} />
      </div>
      {Object.entries(config.otel?.scara_phi ?? {}).map(([k, v]) => (
        <Rand
          key={k}
          label={`Ø ${k} mm`}
          unit={t("struct.ret.ori_pret_referinta")}
          value={v}
          onChange={(x) => set(["otel", "scara_phi", k], x)}
        />
      ))}
      {Object.entries(config.otel?.fasonare_familie_eur_kg ?? {}).map(
        ([k, v]) => (
          <Rand
            key={k}
            label={t("struct.ret.fasonare_familie", { forma: forma(k) })}
            unit="€/kg"
            value={v}
            onChange={(x) => set(["otel", "fasonare_familie_eur_kg", k], x)}
          />
        ),
      )}
      <Rand
        label={t("struct.ret.fasonare_per_bucata")}
        hint={t("struct.ret.0_oprita_aprinde")}
        unit={t("struct.ret.um_buc")}
        value={config.otel?.fasonare_eur_buc ?? 0}
        onChange={(x) => set(["otel", "fasonare_eur_buc"], x)}
      />
      {Object.entries(config.otel?.ore_om_kg_familie ?? {}).map(([k, v]) => (
        <Rand
          key={k}
          label={t("struct.ret.montaj_familie", { forma: forma(k) })}
          unit={t("struct.ret.um_ore_om_kg")}
          value={v}
          onChange={(x) => set(["otel", "ore_om_kg_familie", k], x)}
        />
      ))}

      <div className="rc-sect">
        {t("struct.ret.manopera_raportul_dintre")}
        <Lamurire text={t("struct.ret.nivelul_decide_pretul")} />
      </div>
      {Object.entries(config.manopera_eur_h ?? {}).map(([k, v]) => (
        <Rand
          key={k}
          label={spec(k)}
          unit={t("struct.ret.um_ora_om")}
          value={v}
          onChange={(x) => set(["manopera_eur_h", k], x)}
        />
      ))}

      <div className="rc-sect">{t("struct.ret.utilaje_operator_unde")}</div>
      {Object.entries(config.utilaje_eur_h ?? {}).map(([k, v]) => (
        <Rand
          key={k}
          label={utilaj(k)}
          unit={t("struct.ret.um_ora")}
          value={v}
          onChange={(x) => set(["utilaje_eur_h", k], x)}
        />
      ))}

      <div className="rc-sect">{t("struct.ret.materiale_fara_reper")}</div>
      {Object.entries(config.materiale_eur ?? {}).map(([k, v]) => (
        <Rand
          key={k}
          label={material(k)}
          unit="€"
          value={v}
          onChange={(x) => set(["materiale_eur", k], x)}
        />
      ))}

      <div className="rc-sect">{t("struct.ret.cost_direct_pret")}</div>
      {Object.entries(config.indirecte_pct ?? {}).map(([k, v]) => (
        <Rand
          key={k}
          label={indirect(k)}
          unit="%"
          value={v}
          onChange={(x) => set(["indirecte_pct", k], x)}
        />
      ))}

      <div className="rc-sect">{t("struct.ret.cursul_afisare")}</div>
      <Rand
        label={t("struct.ret.curs_leu_euro")}
        hint={t("struct.ret.informativ_afisare_p1", { p1: config.curs_lei_eur?.la, p2: config.curs_lei_eur?.sursa })}
        unit="lei/€"
        value={config.curs_lei_eur?.valoare ?? 4.98}
        onChange={(x) => set(["curs_lei_eur", "valoare"], x)}
      />

      <div className="pf-actions">
        <button className="sg-primary" disabled={busy || !potScrie} onClick={onSave}>
          {t("struct.ret.salveaza")}
        </button>
        {!implicit && (
          <button disabled={busy || !potScrie} onClick={onReset}>
            {t("struct.ret.revino_valorile_implicit")}
          </button>
        )}
        {saved && <span className="pf-saved">{t("struct.ret.salvat")}</span>}
        {error && <span className="sg-error">{error}</span>}
      </div>
    </div>
  );
}
